import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  switchMap,
  take
} from "rxjs";
import { Logger } from "shared";
import { MediaViewerOverlayService } from "./components/media-overlay/media-viewer-overlay.service";
import { ImageLink, Media, MediaDirectory } from "./models/media";


@Injectable({
  providedIn: 'root'
})
export class MediaService {

  protected entities$ = new BehaviorSubject<Media[]>([]);
  protected logger = new Logger('MediaService');

  protected timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  constructor(protected httpClient: HttpClient,
              protected mediaViewerOverlayService: MediaViewerOverlayService) {
    this.logger.log('MediaService.ctor');
  }

  getMediaById(id: string, defaultMedia?: Media): Observable<Media | null> {
    if (this.entities$.value.length) {
      return this.entities$.pipe(
        switchMap(entities => {
          return of(entities.find(media => media.id === id) ?? defaultMedia ?? null);
        }),
        catchError((error) => {
          this.logger.error(`Error getting media entity: ${error}`);
          return of(defaultMedia ?? null);
        }),
        take(1)
       )
    }
    return of(defaultMedia ?? null);
  }

  loadMedia(language: string, area: string, path?: string): Observable<Media[]> {
    let params = new HttpParams();
    if (language) {
      params = params.set('locale', language);
    }
    if (area) {
      params = params.set('area', area);
    }
    if (path) {
      params = params.set('path', path);
    }
    params = params.set('timezone', this.timezone);
    return this.httpClient.get<Media[]>('/media', { params }).pipe(
      catchError((error) => {
        this.logger.error(`Error getting media entities: ${error}`);
        return of([]);
      }),
      map((result) => {
        this.logger.debug(`Received ${result.length} media entities`);
        this.entities$.next(result);
        return result;
      })
    );
  }

  /**
   * Lists one directory of the media tree for a media item of type `explorer`.
   * `path` is relative to the area root (e.g. `wpgh_ads/wpgh_campaign_aug_01`)
   * and comes either from the item's `properties.explorer` or from a directory
   * entry of a previous listing.
   */
  browse(language: string, area: string, path: string): Observable<MediaDirectory> {
    let params = new HttpParams()
      .set('area', area)
      .set('path', path || '');
    if (language) {
      params = params.set('locale', language);
    }
    return this.httpClient.get<MediaDirectory>('/media/browse', { params });
  }

  setReaction(mediaId: string, reaction: string): Promise<void> {
    return new Promise((resolve,reject) => {
      this.httpClient.post<Media>(`/reaction/media/${mediaId}`, {
        reaction_type: reaction
      }).subscribe({
        next: (response: Media) => {
          if (response) {
            resolve();
          } else {
            reject('railed to set media reaction');
          }
        }, 
        error: (error) => { reject(error) }
      })
    });
  }

  getBadgeInfo(media: Media): { label: string, class: string } | undefined {
    if (media?.displayBadges && !media.properties?.played) {
      return { label:'media.badge.new', class:'new' };
    }
    return undefined;
  }

  getCoverSrc(media: Media, options?: { maxWidth?: number, fallbackToDefault?: boolean }): string {
    return media.cover?.src || 
          media.links?.[0]?.src || 
          (options?.fallbackToDefault ? '/assets/images/default-media.jpg' : '');
  }

  getCoverHeight(media: Partial<Media>, defaultHeightPct = 9/16 * 100): number {
    if (!!media) {
      const roundToTwo = (value: number) => Number.parseFloat(value.toFixed(4));
      if (media.cover && media.cover.width > 0 && media.cover.height > 0) {
        let ratio = media.cover.height/media.cover.width;
        let pct   = 100 * ratio;
        return roundToTwo(pct);
      } else if (media.links && media.links.length > 0) {
        const link: ImageLink = media.links.find(link => 
          link.mimeType.startsWith['image/'] && 
          link.width > 0 && 
          link.height > 0
        );
        if (!!link) {
          let ratio = link.height/link.width;
          let pct   = 100 * ratio;
          return roundToTwo(pct);
        } else if (media.type=='binary') {
          if (media.links[0].mimeType.startsWith('application/pdf')) {
            return defaultHeightPct;
          }
        } else { //if (media.type=='audio') {
          return defaultHeightPct;
        }
      }
    }
    return 0;
  }
}
